import { checkStatus, parseJSON, str2ab, requestRaw } from "utils/request";
import { ab2str } from "utils/request";
import { baseRequestURL } from "api/BaseAPI";
import { isObject } from "types/base";
export class AbstractClient {
    baseUrl;
    headers;
    constructor(baseUrl, headers = {}) {
        this.baseUrl = baseUrl;
        this.headers = headers;
    }
    getBaseUrl() {
        return this.baseUrl;
    }
    getHeaders() {
        return this.headers;
    }
    get(url) {
        return this.makeRequestRaw("GET", url).then((parseJSON));
    }
    post(url, data) {
        return this.makeRequestRaw("POST", url, data).then((parseJSON));
    }
    patch(url, data) {
        return this.makeRequestRaw("PATCH", url, data).then((parseJSON));
    }
    put(url, data) {
        return this.makeRequestRaw("PUT", url, data).then((parseJSON));
    }
    delete(url) {
        return this.makeRequestRaw("DELETE", url).then((parseJSON));
    }
    makeRequestRaw(method, url, data, skipCheckStatus = false) {
        return this.request(url, this._init(method, data)).then((res) => {
            if (skipCheckStatus) {
                return res;
            }
            return checkStatus(res);
        });
    }
    _init(method, data) {
        const init = { method };
        let headers = this.headers;
        if (data instanceof File) {
            headers = new Headers(this.headers);
            init.body = data;
            let dataType = data.type;
            if (data.type === "application/x-gzip" || String(data.name).slice(-2).toLowerCase() === "gz") {
                dataType = "application/octet-stream";
            }
            headers.append("Content-Type", dataType);
        }
        else if (isObject(data)) {
            headers = new Headers(this.headers);
            init.body = JSON.stringify(data);
            headers.append("Content-Type", "application/json");
        }
        else if (typeof data === "string" || data instanceof FormData) {
            init.body = data;
        }
        else if (typeof data === "object") {
            headers = new Headers(this.headers);
            init.body = JSON.stringify(data);
            headers.append("Content-Type", "application/json");
        }
        init.headers = headers;
        return init;
    }
    checkAndParse(response, noLogout, noErrorToast, isLogoutCheck) {
        return checkStatus(response, noLogout, noErrorToast, isLogoutCheck).then((response) => parseJSON(response));
    }
}
export class DefaultClient extends AbstractClient {
    request(url, init, disableAuth) {
        const requestUrl = url.startsWith("http") ? url : this.baseUrl + url;
        return requestRaw(requestUrl, init, disableAuth);
    }
}
export class CustomerClient extends AbstractClient {
    account;
    constructor(baseUrl, headers = {}, account) {
        super(baseUrl, headers);
        this.account = account;
    }
    request(url, init) {
        return this._request(url, init, 500);
    }
    async _request_private_key(options) {
        const isRelay = this.account?.configuration?.features?.includes("communication-relay") && this.account?.id !== undefined;
        const isEncryptedRelay = isRelay && !this.account?.configuration?.features?.includes("disable-relay-encryption");
        let privateKey = undefined;
        if (isEncryptedRelay) {
            const subtle = window.crypto.subtle;
            // on an request to the API proxy
            // we will make the ephemeral key pair
            const keyPair = await subtle.generateKey({
                name: "RSA-OAEP",
                modulusLength: 2048,
                publicExponent: new Uint8Array([1, 0, 1]),
                hash: "SHA-256"
            }, true, ["encrypt", "decrypt"]);
            // export-import the same key to obtain signing abilities
            const exported_private_key = await subtle.exportKey("pkcs8", keyPair.privateKey);
            const exported_public_key = await subtle.exportKey("spki", keyPair.publicKey);
            const signable_private_key = await subtle.importKey("pkcs8", exported_private_key, { name: "RSA-PSS", hash: "SHA-256" }, true, ["sign"]);
            const exportPublicKey = "-----BEGIN PUBLIC KEY-----\n" + btoa(ab2str(exported_public_key)) + "\n-----END PUBLIC KEY-----\n";
            const signatureBuffer = await subtle.sign({ name: "RSA-PSS", saltLength: 32 }, signable_private_key, str2ab(exportPublicKey));
            const exportPublicKeySignature = btoa(ab2str(signatureBuffer));
            const public_key_as_base64 = `public-key=base64,${btoa(exportPublicKey)}.${exportPublicKeySignature};encoding=rsa-chunked`;
            const updatedHeaders = new Headers(options.headers);
            updatedHeaders.set("Content-Encryption", public_key_as_base64);
            options.headers = updatedHeaders;
            if (typeof options?.body === "string") {
                const e = new TextEncoder();
                const signedBody = await subtle.sign({ name: "RSA-PSS", saltLength: 32 }, signable_private_key, e.encode(options?.body));
                options.body = `${options?.body}.${btoa(ab2str(signedBody))}`;
            }
            privateKey = keyPair.privateKey;
        }
        return privateKey;
    }
    async _request(url, init, timeout = 1000) {
        const requestUrl = url.startsWith("http") ? url : this.baseUrl + url;
        const options = {
            ...init
            //mode: "no-cors",
        };
        const privateKey = await this._request_private_key(options);
        return requestRaw(requestUrl, options, undefined, true, undefined, privateKey);
    }
}
export class ApiClient {
    base;
    constructor(base) {
        this.base = base;
    }
}
const DEFAULT_CLIENT = new DefaultClient(baseRequestURL + "v1/");
export class ClientFactory {
    static getBaseClient() {
        return DEFAULT_CLIENT;
    }
}
